<template>
  <div class="view-order">
    <div class="title-container">
      <h3 class="page-title">
        {{ title }}
      </h3>
      <div v-if="canEditOrder">
        <BackstageButton data-cy-button="order-edit-button" v-on:click="edit()" :icon-right="Icons.EDIT_WHITE">
          {{ $t('viewOrder.edit_order') }}
        </BackstageButton>
      </div>
    </div>
    <ViewEditOrders
      :id="id"
      :order-is-draft="isDraft"
      :show-related-orders="showRelatedOrders"
      v-on:update-orders="updateOrder"
      v-on:orders-are-valid="setIsValidOrder" />
    <div class="form">
      <div v-if="order" class="buttons-cnt buttons-cnt-divisor">
        <div class="fields">
          <div v-if="canEditOrder" class="field right">
            <BackstageButton v-if="isDraft" :variant="ButtonVariant.SECONDARY" v-on:click="cancel()">
              {{ $t('global.cancel_button') }}
            </BackstageButton>
            <div>
              <BackstageButton
                v-if="isDraft"
                :disabled="salesOrdersDisabled || invalid"
                :class="[{ submitting: submitting }]"
                v-on:click="!invalid && submit()">
                {{ isDraft ? $t('viewOrder.submit_button') : $t('viewOrder.edit_button') }}
              </BackstageButton>
              <div v-if="!isDraft" class="order-update-note">
                {{ $t('viewOrder.update_order_note') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isDraft && showSubmitPopup">
      <NotificationMessage :title="$t('viewOrder.submit_reminder_popup_title')" :size="'small'" v-on:close="closePopup">
        <SubmitReminder
          :submitting="submitting"
          :submit-enabled="!salesOrdersDisabled && isValidOrder && !saving"
          :save-draft-enabled="!salesOrdersDisabled && isValidOrder"
          :deleting="deleting"
          :saving="saving"
          v-on:save="saveDraft"
          v-on:delete="deleteDraft"
          v-on:submit="submit" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import SubmitReminder from '@/components/common/submit-reminder.vue';
import ViewEditOrders from '@/components/orders/view-edit-orders.vue';
import { useAnalytics } from '@/composables/analytics';
import { useBPLogger } from '@/composables/business-process-logger';
import { OrderEditType } from '@/constants/orderEditType';
import { ordersMixins } from '@/mixins/orders.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { ButtonVariant } from '@/models/button-variant.enum';
import { Feature } from '@/models/feature.model';
import { GaCheckoutStepNames, GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { Order } from '@/models/order.model';
import { DraftsService } from '@/services/drafts.service';
import { FeaturesService } from '@/services/features.service';
import { OrdersService } from '@/services/orders.service';
import { useUserStore } from '@/stores/user';
import { DraftModel, ValidatorResult } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { RouteNames } from '../router';

export default defineComponent({
  name: 'view-order',
  components: {
    SubmitReminder,
    ViewEditOrders,
    NotificationMessage,
    BackstageButton,
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    orderIsDraft: {
      type: Boolean,
      default: undefined,
      required: false,
    },
    showRelatedOrders: {
      type: Boolean,
      default: undefined,
      required: false,
    },
    tryClose: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { selectedAccountId } = storeToRefs(useUserStore());
    const { pageViewBPLog } = useBPLogger();
    const { addCustomGaEvent } = useAnalytics();
    const { isSalesRep } = useUserStore();

    return {
      route,
      router,
      ButtonVariant,
      accountId: selectedAccountId,
      addCustomGaEvent,
      pageViewBPLog,
      isSalesRep,
      Icons,
    };
  },
  data() {
    return {
      loading: true,
      saving: false,
      submitting: false,
      deleting: false,
      showSubmitPopup: false,
      invalid: false,
      isValidOrder: true,
      order: undefined as Order | undefined,
      draftsService: new DraftsService(),
      ordersService: new OrdersService(),
      featuresService: new FeaturesService(),
      salesOrdersDisabled: true,
    };
  },
  computed: {
    canEditOrder(): boolean {
      return (
        !!this.order &&
        (this.theOrderIsDraft(this.order) || (this.isSalesRep() && this.isOrderEditableForSalesReps(this.order)))
      );
    },
    isDraft(): boolean {
      return this.orderIsDraft || this.$route.path.indexOf('drafts') !== -1;
    },
    title(): string {
      return this.router.currentRoute.value.query.showRelatedOrders === 'true'
        ? ''
        : this.order
          ? `${this.order?.orderNumber} - ${this.$t('viewOrder.title')}`
          : '';
    },
  },
  watch: {
    tryClose(newVal) {
      this.showSubmitPopup = newVal;

      if (newVal && !this.isDraft) {
        this.goBackToOrders();
      }
    },
    accountId: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$router.replace({ name: RouteNames.Home });
      }
    },
  },
  mounted() {
    console.log(this.pageViewBPLog(true, { page_name: 'view-order', order_number: this.order?.orderNumber }));
    this.determineWhetherSalesOrdersAreDisabled();
  },
  methods: {
    ...utilsMixins,
    ...ordersMixins,
    goBackToOrders() {
      this.$router.push('/orders');
    },
    closePopup() {
      this.$emit('cancel');
      this.showSubmitPopup = false;
    },
    updateOrder(updatedOrders: Order[]) {
      this.order = updatedOrders.find((order) => order.selected);
    },
    async saveDraft() {
      if (this.saving) {
        return;
      }

      this.saving = true;
      const response = await this.draftsService.update(this.order as unknown as DraftModel, this.accountId);

      if (response.isValid()) {
        this.goBackToOrders();
      }

      this.saving = false;
    },
    async deleteDraft() {
      if (!this.order || this.deleting) {
        return;
      }

      this.deleting = true;
      const response = await this.draftsService.remove(this.order.id, this.accountId);

      if (response.isValid()) {
        // ToDo: show success message
        this.goBackToOrders();
      }

      this.deleting = false;
    },
    setIsValidOrder(orderIsValid: boolean) {
      this.isValidOrder = orderIsValid;
    },
    cancel() {
      this.goBackToOrders();
    },
    edit() {
      const orderType = this.isDraft ? OrderEditType.Draft : OrderEditType.Order;

      this.$router.push(
        `/orders/${this.order?.orderNumber ?? this.id}/edit-products/${orderType}/${
          AddEditOrderBreadcrumbSteps.productsModelsSelection
        }`,
      );
    },
    async submit() {
      if (!this.submitting) {
        this.submitting = true;

        const response = await this.draftsService.submit(this.order as unknown as DraftModel, this.accountId);

        if (response.isValid()) {
          this.showSuccessNotification(response);

          this.addCustomGaEvent({
            event: GaCustomEvents.CheckoutStep5,
            checkout_step_name: GaCheckoutStepNames.Confirmation,
          });

          this.addCustomGaEvent({
            event: GaCustomEvents.Purchase,
          });
        }

        this.submitting = false;
      }
    },
    async determineWhetherSalesOrdersAreDisabled() {
      this.salesOrdersDisabled = await this.featuresService.hasFeature(Feature.SALES_ORDERS_DISABLED, this.accountId);
    },
    showSuccessNotification(response: ValidatorResult<DraftModel>) {
      const confirmedOrderNumber = response.model?.orderNumber;
      const orderType = response.model?.type;

      this.$router.replace(`/success-ordered/${orderType}/${confirmedOrderNumber}`);
    },
  },
});
</script>
<style scoped lang="scss">
.title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.view-order {
  @include common-container;
  padding-bottom: 60px;

  .page-title {
    padding: 0 0 8px;
  }

  .order-update-note {
    font-size: 0.9em;
    max-width: 300px;
  }
}
</style>
